import { useCallback, useState } from 'react';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import type { Entity } from '@/types/Entity';
import { httpClient, isApiError } from '@/utils/httpClient';
import { useNotifications } from '@/stores/useNotificationStore';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';

interface InactivateIeKitDialogProps {
  open: boolean;
  kit: Entity | null | undefined;
  onClose: () => void;
}

interface InactivateResult {
  kit_items_inactivated: number;
  hand_receipts_cleared: number;
  assignments_removed: number;
}

export const InactivateIeKitDialog = ({ open, kit, onClose }: InactivateIeKitDialogProps) => {
  const queryClient = useQueryClient();
  const { success: notifySuccess, error: notifyError } = useNotifications();
  const { label: ieKitLabel } = useIeKitLabels('front_window');
  const isViewer = useWriteGuard();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (!kit) return;

    setIsSubmitting(true);
    try {
      const response = await httpClient.post<InactivateResult[]>(
        '/api/v1/rpc/inactivate_ie_kit',
        { p_ie_kit_id: kit.id },
      );

      const result = Array.isArray(response) ? response[0] : response;

      await queryClient.invalidateQueries({ queryKey: ['entities'] });
      await queryClient.invalidateQueries({ queryKey: ['ie-summary'] });

      const parts: string[] = [];
      if (result?.kit_items_inactivated) {
        parts.push(`${result.kit_items_inactivated} kit item(s)`);
      }
      if (result?.hand_receipts_cleared) {
        parts.push(`${result.hand_receipts_cleared} hand receipt(s) updated`);
      }
      if (result?.assignments_removed) {
        parts.push(`${result.assignments_removed} assignment(s) removed`);
      }
      const detail = parts.length > 0 ? ` (${parts.join(', ')})` : '';

      notifySuccess(`${ieKitLabel} Inactivated`, `"${kit.display_name}" has been inactivated.${detail}`);
      onClose();
    } catch (err: unknown) {
      const message = isApiError(err) || err instanceof Error ? err.message : `Failed to inactivate ${ieKitLabel}.`;
      notifyError('Inactivate Failed', message);
    } finally {
      setIsSubmitting(false);
    }
  }, [kit, queryClient, notifySuccess, notifyError, onClose]);

  const kitName = kit?.display_name ?? kit?.entity_identifier ?? `this ${ieKitLabel.toLowerCase()}`;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (isSubmitting) return;
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') onClose();
      }}
      disableEscapeKeyDown={isSubmitting}
      maxWidth="xs"
      aria-labelledby="inactivate-ie-kit-title"
      aria-describedby="inactivate-ie-kit-description"
    >
      <DialogTitle
        id="inactivate-ie-kit-title"
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <WarningIcon color="warning" />
        Inactivate {ieKitLabel}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="inactivate-ie-kit-description">
          Are you sure you want to inactivate <strong>{kitName}</strong>? This will remove it from
          active planning, clear {ieKitLabel.toLowerCase()} references from hand receipts, and remove all person
          assignments.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
          Cancel
        </Button>
        <ViewerGuardedButton
          onClick={() => void handleConfirm()}
          color="warning"
          variant="contained"
          disabled={isSubmitting || isViewer}
        >
          {isSubmitting ? 'Inactivating...' : 'Yes, Inactivate'}
        </ViewerGuardedButton>
      </DialogActions>
    </Dialog>
  );
};
